'use client';

import { Loader2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../../../components/ui/button';
import { Textarea } from '../../../components/ui/textarea';
import { CommentData } from '../../../types/shared';
import { useRealtime, useRealtimeComments } from '../contexts/RealtimeProvider';
import { addTrafficComment } from '../lib/actions/trafficActions';
// 👇 Импортируем созданный Server Action
// import { useRealtime } from '../contexts/RealtimeProvider';
// import { addTrafficComment } from '../lib/actions/trafficCommentActions';

interface FormProps {
  reportId: string;
  parentId?: string | null;
  onCommentAdded?: (newComment: CommentData) => void;
}

export function TrafficAddCommentForm({ reportId, parentId = null, onCommentAdded }: FormProps) {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addOptimisticComment } = useRealtimeComments();

  // Функция отправки
  const handleSubmit = useCallback(async () => {
    const trimmedText = text.trim();
    if (!trimmedText || isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Вызываем серверный экшен
      const result = await addTrafficComment(reportId, trimmedText, parentId || undefined);

      if (result.status === 'success') {
        setText('');
        toast.success('Комментарий добавлен!');

        if (onCommentAdded) {
          onCommentAdded(result.newComment);
        } else {
          addOptimisticComment(result.newComment);
        }
      } else {
        toast.error(result.message ?? 'Ошибка отправки');
      }
    } catch (error) {
      console.error(error);
      toast.error('Ошибка отправки');
    } finally {
      setIsSubmitting(false);
    }
  }, [text, reportId, parentId, isSubmitting, addOptimisticComment, onCommentAdded]);

  // Обработка Ctrl+Enter
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault(); // Чтобы не было переноса строки
      handleSubmit();
    }
  };

  return (
    <form
      onSubmit={(e) => {
        // 🔥 КРИТИЧНО ДЛЯ CHROME:
        // Останавливаем нативную отправку формы, иначе браузер перезагрузит страницу
        // или прервет выполнение JS до завершения транзакции.
        e.preventDefault();
        e.stopPropagation();
        handleSubmit();
      }}
      className="flex flex-col gap-2 mt-2"
    >
      <label htmlFor={`traffic-comment-${reportId}-${parentId}`} className="sr-only">
        Ваш комментарий
      </label>

      <Textarea
        id={`traffic-comment-${reportId}-${parentId}`}
        name="text"
        placeholder="Написать комментарий... (Ctrl+Enter)"
        required
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        className="bg-slate-700/50 border-slate-600 focus:ring-sky-500 text-slate-100 placeholder:text-slate-400 min-h-[80px]"
        disabled={isSubmitting}
        rows={3}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          size="sm"
          disabled={isSubmitting || !text.trim()}
          className="bg-sky-600 hover:bg-sky-700 text-white transition-colors"
        >
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isSubmitting ? 'Отправка...' : 'Отправить'}
        </Button>
      </div>
    </form>
  );
}
